import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import useStructuredFilterMap from '@/features/StructuredFilter/configs/useStructuredFilterMap';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useWorker from '@/hooks/pure/useWorker';
import {
  ACCOUNT_COLUMNS,
  AGENT_COLUMNS,
  CONTACT_COLUMNS,
  FEEDBACK_COLUMNS,
  SENTIMENT_COLUMNS,
  SOURCE_COLUMNS,
} from '@/types/filterKeyConst';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';

import {
  type ExtendedFilterQueryFormatT,
  SEARCH_TARGET,
} from './searchQueryBuilderTypes';
import searchQueryUtils from './searchQueryUtils';
import SearchWorker from './searchWorker.ts?worker';

type Props = {
  searchTarget: SEARCH_TARGET;
  debouncedInput: string;
};

const sortedColumnKey = [
  FEEDBACK_COLUMNS.SNIPPET,
  FEEDBACK_COLUMNS.CATEGORY,
  FEEDBACK_COLUMNS.TAG,
  FEEDBACK_COLUMNS.PRIORITY,
  SENTIMENT_COLUMNS.SENTIMENT_DETAIL,
  SOURCE_COLUMNS.SOURCE_TYPE,
  SOURCE_COLUMNS.DATA_ORIGIN,
  ACCOUNT_COLUMNS.SIZE,
  ACCOUNT_COLUMNS.INDUSTRY,
  ACCOUNT_COLUMNS.PLAN,
  CONTACT_COLUMNS.PLAN,
  CONTACT_COLUMNS.TYPE,
  CONTACT_COLUMNS.STAGE,
  AGENT_COLUMNS.ALIAS,
  AGENT_COLUMNS.ROLE,
];

const useSearchQueryData = (props: Props) => {
  const filterMap = useStructuredFilterMap();

  const { mappedColumn, fullColumn } = useMappedColumn();

  const workerPayload = useMemo(() => {
    return { column: mappedColumn, input: props.debouncedInput };
  }, [mappedColumn, props.debouncedInput]);

  const enabled = useMemo(() => {
    return props.debouncedInput.trim() !== '';
  }, [props.debouncedInput]);

  const { data: workerData, isLoading: isWorkerLoading } = useWorker<
    ExtendedFilterQueryFormatT[]
  >({
    workerUrl: SearchWorker,
    payload: workerPayload,
    enabled: enabled && props.searchTarget === SEARCH_TARGET.DEFAULT,
  });

  const fetcher = useCallback(
    async (target: SEARCH_TARGET): Promise<ExtendedFilterQueryFormatT[]> => {
      const result = structuredFilterUtils.getFieldInfo(target, filterMap);
      const col = result?.fieldInfo;
      if (!col) throw new Error(`Column ${target} not found`);
      if (col.filterItemOptions?.filterItemFetcher) {
        const res = await col.filterItemOptions.filterItemFetcher({
          searchValue: props.debouncedInput,
        });

        return (
          res?.map((item) => {
            return {
              columnKey: col.columnKey,
              filterValues: [
                {
                  filterKey: item.filterKey,
                  filterName: item.searchValue || item.filterName,
                },
              ],
              operator: STRUCTURED_FILTER_OPERATOR.AND,
              columnName: col.columnName,
              domain: searchQueryUtils.getDomain(col.columnKey),
            };
          }) || []
        );
      }
      return [];
    },
    [filterMap, props.debouncedInput],
  );

  const { data: remoteData, isLoading: remoteLoading } = useQuery({
    queryKey: ['searchQueryData', props.searchTarget, props.debouncedInput],
    queryFn: () => fetcher(props.searchTarget),
    enabled: enabled && props.searchTarget !== SEARCH_TARGET.DEFAULT,
  });

  const combinedQuery = useMemo(() => {
    switch (props.searchTarget) {
      case SEARCH_TARGET.DEFAULT:
        return {
          data: workerData,
          isLoading: isWorkerLoading,
        };
      default:
        return {
          data: remoteData || null,
          isLoading: remoteLoading,
        };
    }
  }, [
    props.searchTarget,
    workerData,
    isWorkerLoading,
    remoteData,
    remoteLoading,
  ]);

  return {
    fullColumn,
    data: combinedQuery.data,
    isLoading: combinedQuery.isLoading,
  };
};

type MappedColumnT = {
  columnKey: string;
  columnName: string;
  filterList: StructuredFilterItem[];
};

const useMappedColumn = () => {
  const filterMap = useStructuredFilterMap();

  const fullColumn: StructuredFieldOptionMap = useMemo(() => {
    return {
      ...filterMap,
    };
  }, []);

  const [mappedColumns, setMappedColumns] = useState<MappedColumnT[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const columns = [];
      for (const columnKey of sortedColumnKey) {
        const result = structuredFilterUtils.getFieldInfo(
          columnKey,
          fullColumn,
        );
        const col = result?.fieldInfo;
        if (!col) throw new Error(`Column ${columnKey} not found`);
        const flattened = await col.filterItemOptions?.filterItemFetcher?.();
        let flattenList: StructuredFilterItem[] = [];

        const mapper = async (item: StructuredFilterItem) => {
          if (item.childOptions?.fetcher) {
            const children = await item.childOptions.fetcher(item.filterKey);
            for (const child of children) {
              flattenList.push({
                columnKey: child.columnKey,
                filterKey: child.filterKey,
                filterName: child.searchValue || child.filterName,
              });
              await mapper(child);
            }
          }
        };

        for (const item of flattened || []) {
          flattenList.push({
            columnKey: item.columnKey,
            filterKey: item.filterKey,
            filterName: item.searchValue || item.filterName,
          });
          await mapper(item);
        }

        columns.push({
          columnKey: col.columnKey,
          columnName: col.columnName,
          filterList: flattenList || [],
        });
      }
      setMappedColumns(columns);
    };

    fetchData();
  }, [fullColumn]);

  return {
    mappedColumn: mappedColumns,
    fullColumn: fullColumn,
  };
};

export default useSearchQueryData;
